/**
 * Utility functions for ProductionReport feature
 */

import { GALLONS_PER_ACRE_FOOT } from "./constants";

/**
 * Check if a row/name is "not aggregated" / "no permit"
 * @param {string|Object} nameOrRow - Agg system name string, or row with agg_system_name
 * @returns {boolean} True if not aggregated
 */
export const isNotAggregated = (nameOrRow) => {
  const name =
    typeof nameOrRow === "string" ? nameOrRow : nameOrRow?.agg_system_name;
  const lower = (name || "").toLowerCase();
  return lower === "not aggregated" || lower === "no permit";
};

/**
 * Format a number as gallons (rounded, with commas)
 * @param {number|null|undefined} num - The number to format
 * @returns {string} Formatted string or "-" if null/undefined
 */
export const formatGallons = (num) => {
  if (num === null || num === undefined) return "-";
  return Math.round(Number(num)).toLocaleString();
};

/**
 * Format a permit value (2 decimal places)
 * @param {number|null|undefined} num - The number to format
 * @returns {string} Formatted string or "-" if null/undefined
 */
export const formatPermit = (num) => {
  if (num === null || num === undefined) return "-";
  return Number(num).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format acre-feet value (2 decimal places)
 * @param {number|null|undefined} num - The number to format
 * @returns {string} Formatted string or "-" if null/undefined
 */
export const formatAcreFeet = (num) => {
  if (num === null || num === undefined) return "-";
  return Number(num).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format a decimal as percentage
 * @param {number|null|undefined} num - The decimal number (0-1 range)
 * @returns {string} Formatted percentage string or "-" if invalid
 */
export const formatPercent = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "-";
  return `${(Number(num) * 100).toFixed(2)}%`;
};

/**
 * Safely add two numbers, treating null/undefined as 0
 * @param {number|null|undefined} a - First number
 * @param {number|null|undefined} b - Second number
 * @returns {number} Sum of the two numbers
 */
export const safeAdd = (a, b) => (Number(a) || 0) + (Number(b) || 0);

/**
 * Get status based on percentage of permit used
 * @param {number} pct - Percentage as decimal (0-1+)
 * @returns {"danger"|"warning"|"success"} Status string for styling
 */
export const getPercentStatus = (pct) => {
  if (pct > 1) return "danger";
  if (pct > 0.8) return "warning";
  return "success";
};

/**
 * Compute percentage of permit used
 * @param {Object} totals - Object with h_permit_value_gal, o_permit_value_gal, ytd_gal
 * @returns {number} Percentage as decimal
 */
export const computePctPermit = (totals) => {
  const totalPermitGal = totals.h_permit_value_gal + totals.o_permit_value_gal;
  if (totalPermitGal === 0) return 0;
  return totals.ytd_gal / totalPermitGal;
};

/**
 * Calculate remaining gallons
 * @param {Object} row - Row with h_permit_value_gal, o_permit_value_gal, ytd_gal
 * @returns {number} Remaining gallons
 */
export const calcRemainingGal = (row) => {
  return (
    (row.h_permit_value_gal || 0) +
    (row.o_permit_value_gal || 0) -
    (row.ytd_gal || 0)
  );
};

/**
 * Calculate remaining acre-feet
 * @param {Object} row - Row with permit and production data
 * @returns {number} Remaining acre-feet
 */
export const calcRemainingAf = (row) => {
  return calcRemainingGal(row) / GALLONS_PER_ACRE_FOOT;
};

/**
 * Compare two agg system names, with "Not Aggregated" / "No Permit" always sorting to the bottom
 * @param {string} a - First agg system name
 * @param {string} b - Second agg system name
 * @returns {number} Comparison result
 */
export const compareAggSystems = (a, b) => {
  const aNotAgg = isNotAggregated(a);
  const bNotAgg = isNotAggregated(b);
  if (aNotAgg && !bNotAgg) return 1;
  if (!aNotAgg && bNotAgg) return -1;
  return (a || "").localeCompare(b || "");
};

/**
 * Get comparator function for sorting
 * @param {"asc"|"desc"} order - Sort order
 * @param {string} orderBy - Column to sort by
 * @returns {Function} Comparator function
 */
export const getComparator = (order, orderBy) => {
  return (a, b) => {
    let aVal, bVal;

    // Handle computed columns
    if (orderBy === "remaining_gal") {
      aVal = calcRemainingGal(a);
      bVal = calcRemainingGal(b);
    } else if (orderBy === "remaining_af") {
      aVal = calcRemainingAf(a);
      bVal = calcRemainingAf(b);
    } else if (orderBy === "pct_permit") {
      const aTotalPermit =
        (a.h_permit_value_gal || 0) + (a.o_permit_value_gal || 0);
      const bTotalPermit =
        (b.h_permit_value_gal || 0) + (b.o_permit_value_gal || 0);
      aVal = aTotalPermit > 0 ? (a.ytd_gal || 0) / aTotalPermit : 0;
      bVal = bTotalPermit > 0 ? (b.ytd_gal || 0) / bTotalPermit : 0;
    } else {
      aVal = a[orderBy];
      bVal = b[orderBy];
    }

    // Handle nulls
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    // Compare
    let comparison;
    if (typeof aVal === "string") {
      comparison = aVal.localeCompare(bVal);
    } else {
      comparison = aVal - bVal;
    }

    return order === "desc" ? -comparison : comparison;
  };
};

/**
 * Derive filter options from rows
 * @param {Array} rows - Array of data rows
 * @returns {Object} Filter options object
 */
export const deriveFilterOptions = (rows) => {
  if (!rows || rows.length === 0) {
    return {
      years: [],
      aquifers: [],
      aggSystems: [],
      wells: [],
      stateWells: [],
      wellNames: [],
    };
  }
  const years = [...new Set(rows.map((r) => r.report_year))].sort(
    (a, b) => b - a
  );
  const aquifers = [
    ...new Set(rows.map((r) => r.aquifer_name).filter(Boolean)),
  ].sort();
  const aggSystems = [
    ...new Set(rows.map((r) => r.agg_system_name).filter(Boolean)),
  ].sort();
  const wells = [
    ...new Set(rows.map((r) => r.cuwcd_well_number).filter(Boolean)),
  ].sort();
  const stateWells = [
    ...new Set(rows.map((r) => r.state_well_number).filter(Boolean)),
  ].sort();
  const wellNames = [
    ...new Set(rows.map((r) => r.well_name).filter(Boolean)),
  ].sort();
  return { years, aquifers, aggSystems, wells, stateWells, wellNames };
};

/**
 * Apply column filters to rows
 * @param {Array} rows - Array of data rows
 * @param {Object} columnFilters - Filter selections
 * @param {boolean} initialized - Whether filters are initialized
 * @returns {Array} Filtered rows
 */
export const applyColumnFilters = (rows, columnFilters, initialized) => {
  if (!rows || rows.length === 0) return [];
  if (!initialized) return rows;

  const yearsSet = new Set(columnFilters.years);
  const aquifersSet = new Set(columnFilters.aquifers);
  const aggSystemsSet = new Set(columnFilters.aggSystems);
  const wellsSet = new Set(columnFilters.wells);
  const stateWellsSet = new Set(columnFilters.stateWells);
  const wellNamesSet = new Set(columnFilters.wellNames);

  // If any required filter has nothing selected, show no records
  if (
    yearsSet.size === 0 ||
    aquifersSet.size === 0 ||
    aggSystemsSet.size === 0 ||
    wellsSet.size === 0 ||
    wellNamesSet.size === 0
  ) {
    return [];
  }

  return rows.filter((row) => {
    if (!yearsSet.has(row.report_year)) return false;
    if (!aquifersSet.has(row.aquifer_name)) return false;
    if (!aggSystemsSet.has(row.agg_system_name)) return false;
    if (!wellsSet.has(row.cuwcd_well_number)) return false;
    // Only filter by state well if there are options and user has made selections
    if (
      stateWellsSet.size > 0 &&
      row.state_well_number &&
      !stateWellsSet.has(row.state_well_number)
    )
      return false;
    return wellNamesSet.has(row.well_name);
  });
};
